import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Icon } from './icons';

/**
 * Dialog et Drawer (59 §6.1-§6.3) : surfaces temporaires accessibles — rôle `dialog`, titre
 * nommé, Échap pour fermer, focus placé à l'ouverture et **restitué à la fermeture** (59 §13).
 * Un tiroir droit ne démonte pas la page dessous : la liste garde son contexte (59 §6.3).
 *
 * Format (canevas Claude Design « Composant — Boîte de dialogue ») : en-tête sombre à la couleur de la
 * barre du haut, titre Fraunces et sous-titre de contexte ; corps ; pied à filet, phrase d'aide à
 * gauche, actions à droite (secondaire puis principale). Largeurs : sm 420, list 480, md 560, lg 880.
 * Sur téléphone, feuille ancrée en bas.
 */

interface OverlayProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

function useFocusReturn(open: boolean, container: React.RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = container.current?.querySelector<HTMLElement>('[autofocus], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    (first ?? container.current)?.focus();
    return () => previous?.focus();
  }, [open, container]);
}

function useEscape(open: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);
}

/** Tab reste dans la boîte : du dernier élément on revient au premier, et inversement. */
function trapTab(event: ReactKeyboardEvent<HTMLElement>): void {
  if (event.key !== 'Tab') return;
  const focusable = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')];
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function Dialog({
  title,
  subtitle,
  open,
  onClose,
  children,
  footer,
  note,
  size = 'md',
}: OverlayProps & { size?: 'sm' | 'list' | 'md' | 'lg'; subtitle?: ReactNode; note?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusReturn(open, ref);
  useEscape(open, onClose);
  if (!open) return null;
  return (
    <div className="ay-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div ref={ref} className={`ay-dialog ay-dialog--${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={trapTab}>
        <header className="ay-dialog__header">
          <div className="ay-dialog__heading">
            <h2 id={titleId}>{title}</h2>
            {subtitle !== undefined && <p className="ay-dialog__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="ay-dialog__close" aria-label="Fermer" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>
        <div className="ay-dialog__content">{children}</div>
        {(footer !== undefined || note !== undefined) && (
          <footer className="ay-dialog__footer">
            <span className="ay-dialog__note">{note}</span>
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/** Une action de boîte de dialogue : bouton simple, ou envoi du formulaire (`type: 'submit'`). */
export interface DialogAction {
  label: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  busy?: boolean;
  title?: string;
}

/**
 * Boutons d'une boîte de dialogue, toujours dans le même ordre (règle de l'utilisateur du 2026-10-20) :
 * l'action destructive en rouge à gauche (seulement si besoin), une action secondaire à côté, puis
 * « Annuler » juste à gauche de l'action principale, toujours à droite. Le X du haut annule aussi.
 * Aucune boîte ne place ses boutons à la main.
 */
export function DialogActions({ primary, cancel, destructive, secondary }: { primary?: DialogAction | undefined; cancel?: DialogAction | undefined; destructive?: DialogAction | undefined; secondary?: DialogAction | undefined }) {
  const button = (action: DialogAction, className: string) => (
    <button type={action.type ?? 'button'} className={className} onClick={action.onClick} disabled={action.disabled === true || action.busy === true} aria-busy={action.busy} title={action.title}>
      {action.label}
    </button>
  );
  return (
    <div className="ay-dialog-actions">
      <div className="ay-dialog-actions__start">
        {destructive !== undefined && button(destructive, 'ay-button ay-button--danger')}
        {secondary !== undefined && button(secondary, 'ay-button ay-button--secondary')}
      </div>
      <div className="ay-dialog-actions__end">
        {cancel !== undefined && button({ ...cancel, label: cancel.label ?? 'Annuler' }, 'ay-button ay-button--secondary')}
        {primary !== undefined && button(primary, 'ay-button')}
      </div>
    </div>
  );
}

/**
 * Tiroir non modal : un clic dans la vue le ferme. Exceptions : un élément qui ouvre un autre
 * enregistrement (`data-opens-record` : ligne, carte, carte Kanban, événement d'agenda) remplace la
 * fiche sans fermer le tiroir, et un clic dans une boîte de dialogue (ouverte depuis la fiche) le garde.
 */
function useOutsideClose(active: boolean, ref: { current: HTMLElement | null }, onClose: () => void): void {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!active) return;
    const onDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element) || ref.current === null || ref.current.contains(target)) return;
      if (target.closest('[data-opens-record], [role="dialog"], [role="menu"], .ay-overlay') !== null) return;
      close.current();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [active, ref]);
}

export function Drawer({ title, kicker, open, onClose, children, footer, side = 'right', modal = false }: OverlayProps & { side?: 'left' | 'right'; modal?: boolean; kicker?: string }) {
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  useFocusReturn(open, ref);
  useEscape(open, onClose);
  useOutsideClose(open && !modal, ref, onClose);
  if (!open) return null;
  const panel = (
    <aside ref={ref} className={`ay-drawer ay-drawer--${side}`} role="dialog" aria-modal={modal} aria-labelledby={titleId} tabIndex={-1}>
      <header className="ay-drawer__header">
        <div className="ay-drawer__heading">
          {kicker !== undefined && <span className="ay-drawer__kicker">{kicker}</span>}
          <h2 id={titleId}>{title}</h2>
        </div>
        <button type="button" className="ay-icon-button" aria-label="Fermer" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="ay-drawer__content">{children}</div>
      {footer !== undefined && <footer className="ay-drawer__footer">{footer}</footer>}
    </aside>
  );
  return modal ? (
    <div className="ay-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      {panel}
    </div>
  ) : (
    panel
  );
}
